/* eslint-disable prettier/prettier */
import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import classNames from 'classnames';
import { injectIntl } from 'react-intl';
import Hidden from '@material-ui/core/Hidden';

import { ADGMImage } from 'aurora-components';

import Divider from '@material-ui/core/Divider';
import { getPropertyImageName, keyEmptyCheck, getPropertType } from 'aurora-utils/utils';
import messages from 'aurora-translations';
import PropertyBadge from './Badge';
import styles from './styles';

function PropertySummary(props) {
  
  const { classes, intl, locale='en', property, onDelete, onEdit, onDownload, onDetails, hasMarginBottom } = props;

  // eslint-disable-next-line no-shadow
  const getPropertyClassification = property => {
    const propertyType = property?.propertyType ? getPropertType(property.propertyType) : property?.type;
  
    const getLocalizedName = (arName, enName) => locale === 'ar' ? arName : enName;
  
    switch (propertyType) {
      case 'Land':
        if (property?.isVilla) return 'Villa_label';
        if (property?.isBuilding) return 'building';
        return getLocalizedName(property?.landUseNameAr, property?.landUseNameEn);
  
      case 'Building':
        return getLocalizedName(property.buildingClassificationAr, property.buildingClassificationEn) || 'building';
  
      case 'Unit':
        if (property?.isResidentialUnit && property?.isVilla) return 'Villa_label';
        return getLocalizedName(property.unitClassificationAr, property.unitClassificationEn) || 'unit';
  
      default:
        return 'Land';
    }
  };
  

  const propertyEnum = useMemo(() => getPropertyClassification(property), [property]);

  const isBuilding = useMemo(() => property?.propertyType === 2, [property]);

  const isUnit = useMemo(() => property?.propertyType === 4, [property]);
  const isLand = useMemo(() => property?.propertyType === 1, [property]);
 
  const propertyDetails = useMemo(() => {
    return {
      municipality:
        locale === 'en' ? property?.muncipalityEn || property?.municipalityNameEn : property?.muncipalityAr || property?.municipalityNameAr,
      district: locale === 'en' ? property?.districtEn || property?.districtNameEn : property?.districtAr || property?.districtNameAr,
      community: locale === 'en' ? property?.communityEn || property?.communityNameEn : property?.communityAr || property?.communityNameAr,
      plot_address: property?.plotAddress,
    };
  }, [property, locale]);

  const mobilePropertyDetails = useMemo(() => {
    return {
      community: locale === 'en' ? property?.communityEn || property?.communityNameEn : property?.communityEn || property?.communityNameEn,
      district: locale === 'en' ? property?.districtEn || property?.districtNameEn : property?.districtAr || property?.districtNameAr,
      municipality:
        locale === 'en' ? property?.muncipalityEn || property?.municipalityNameEn : property?.municipalityNameAr || property?.muncipalityAr,
    };
  }, [property, locale]);

  const propertyInformation = useMemo(() => {
    return {
      bathrooms: property?.bathrooms > 0 ? property?.bathrooms : null,
      bedrooms: (property?.bedroomCount || property?.bedrooms) > 0 ? property?.bedroomCount || property?.bedrooms : null,
      area: property?.unitArea ? `${property?.unitArea} sqm` : property?.area ? `${property?.area} sqm` : property?.landSize ? `${property?.landSize} sqm` : null,
      units:
        isBuilding && property?.customerOwnedUnitsCount ? `${property?.customerOwnedUnitsCount} ${intl.formatMessage({ ...messages.units })}` :  isBuilding && property?.count ? `${property?.count} ${intl.formatMessage({ ...messages.units })}` : null,
      buildingUsage: isBuilding ? (locale === 'en' ? property?.buildingUsageEn : property?.buildingUsageAr) : null,
      landUseName: isLand || isUnit  ? (locale === 'en' ? property?.landUseNameEn || property?.landUsageEn : property?.landUseNameAr || property?.landUsageAr) : null,
    };
  }, [property, isBuilding, locale]);

  const propertyTitle = useMemo(() => {
    let title = '';
    title =
      locale === 'en'
        ? property?.projectNameEn
          ? property?.projectNameEn
          : property?.districtEn || property?.districtNameEn
        : property?.projectNameAr
        ? property?.projectNameAr
        : property?.districtAr || property?.districtNameAr;

    if (title) {
      return (
        <span>
          {title} , {keyEmptyCheck('plot_label', { p: property?.plotNumber })}
        </span>
      );
    }
    return <span>{keyEmptyCheck('plot_label', { p: property?.plotNumber })}</span>;
  }, [property]);

  return (
    <>
      <div className={classNames(classes.listItemContainer, { [classes.hasMarginBottom]: hasMarginBottom })}>
        <ListItem dense>
          <ListItemText
            id={property.plotId}
            primary={
              <div
                className={classNames(classes.containerItem, {
                  [classes.listItemTextBuilding]: isBuilding,
                })}
              >
                <Grid container>
                  <Grid className={classes.sectionMargin} item xs={12} sm={12} md={7}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexDirection: 'row' }}>
                      <Hidden smDown>
                        <ADGMImage width={72} height={72} name={getPropertyImageName(property)} alt="propertyImage" />
                      </Hidden>
                      <Hidden mdUp>
                        <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                            <ADGMImage width={72} height={72} name={getPropertyImageName(property)} alt="propertyImage" />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                                <Typography className={classes.title}>
                                  {locale === 'en'
                                    ? property?.projectNameEn
                                      ? property?.projectNameEn
                                      : property?.districtEn || property?.districtNameEn
                                    : property?.projectNameAr
                                    ? property?.projectNameAr
                                    : property?.districtAr || property?.districtNameAr}
                                </Typography>

                                {propertyEnum && <Box className={classes.typeChip}>{keyEmptyCheck(propertyEnum)}</Box>}
                              </Box>
                              <Typography className={classes.mobileTitle}>{keyEmptyCheck('plot_label', { p: property?.plotNumber })}</Typography>
                              <Typography className={classes.mobileContent}>
                                <ADGMImage name="Location_property.svg" alt="location" />
                                {Object.values(mobilePropertyDetails)
                                  ?.filter((val) => !!val)
                                  ?.join(', ')}
                              </Typography>
                            </Box>
                          </Box>
                          <Box />
                        </Box>
                      </Hidden>
                      <Hidden smDown>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                            <Typography className={classes.title}>{propertyTitle}</Typography>
                            {propertyEnum && <Box className={classes.typeChip}>{keyEmptyCheck(propertyEnum)}</Box>}
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '6px' }}>
                            {propertyInformation?.landUseName && (
                              <Box className={classes.subTitleBox}>
                                <div className={classes.infoICon} />
                                {propertyInformation.landUseName}
                              </Box>
                            )}
                            {propertyInformation?.buildingUsage && (
                              <Box className={classes.subTitleBox}>
                                <div className={classes.infoICon} />
                                <ADGMImage name="Building_owner.svg" />
                                {propertyInformation.buildingUsage}
                              </Box>
                            )}
                            {propertyInformation?.area && (
                              <Box className={classes.subTitleBox}>
                                <div className={classes.infoICon} />
                                <ADGMImage name="new_ruler.svg" alt="ruler" />
                                {propertyInformation.area}
                              </Box>
                            )}
                            {propertyInformation?.bedrooms && (
                              <Box className={classes.subTitleBox}>
                                <div className={classes.infoICon} />
                                <ADGMImage name="new_bedrooms.svg" alt="ruler" />
                                {propertyInformation.bedrooms}{' '}
                              </Box>
                            )}
                            {propertyInformation?.bathrooms && (
                              <Box className={classes.subTitleBox}>
                                <div className={classes.infoICon} />
                                <ADGMImage name="new_bath.svg" alt="ruler" />
                                {propertyInformation.bathrooms}{' '}
                              </Box>
                            )}
                            {propertyInformation?.units && (
                              <Box className={classes.subTitleBox}>
                                <div className={classes.infoICon} />
                                <ADGMImage name="units_count.svg" />
                                {propertyInformation.units}
                              </Box>
                            )}
                            {propertyInformation?.ownerShipType && (
                              <Box className={classes.subTitleBox}>
                                <div className={classes.infoICon} />
                                {propertyInformation.ownerShipType}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Hidden>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={12} md={5} className={classes.mobileMargin}>
                    <PropertyBadge locale={locale} property={property} />
                  </Grid>
                </Grid>
                <Hidden smDown>
                  <Divider className={classes.divider} />
                </Hidden>
                <Hidden smDown>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box className={classes.listItemContent}>
                      {Object.keys(propertyDetails)?.map((propertyItemKey) =>
                        propertyDetails[propertyItemKey] ? (
                          <div>
                            <Typography className={classes.contentTitle}>{keyEmptyCheck(propertyItemKey)}</Typography>
                            <Typography className={classes.contentSubTitle}>{propertyDetails[propertyItemKey]}</Typography>
                          </div>
                        ) : null
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center' }}>
                      {onEdit && (
                        <Button className={classes.ActionButton} onClick={onEdit}>
                          <ADGMImage name="edit-pencil.svg" alt="edit" />
                        </Button>
                      )}
                      {onDelete && (
                        <Button className={classes.ActionButton} onClick={onDelete}>
                          <ADGMImage name="Trash.svg" alt="delete" />
                        </Button>
                      )}
                      {onDetails && (
                        <Button onClick={onDetails} className={classes.viewAll}>
                          {keyEmptyCheck('view_all_details')}
                          <ADGMImage name="angleRight.svg" alt="angleRight" />
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Hidden>
                <Hidden mdUp>
                  <Divider className={classes.divider} />
                </Hidden>
                <Hidden mdUp>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                      {onEdit && (
                        <Button className={classes.ActionButton} onClick={onEdit}>
                          <ADGMImage name="edit-pencil.svg" alt="edit" />
                        </Button>
                      )}
                      {onDelete && (
                        <Button className={classes.ActionButton} onClick={onDelete}>
                          <ADGMImage name="Trash.svg" alt="delete" />
                        </Button>
                      )}
                      {onDownload && (
                        <Button className={classes.ActionButton} onClick={onDelete}>
                          <ADGMImage name="Download-Icon.svg" alt="delete" />
                        </Button>
                      )}
                    </Box>
                    <Box>
                      {onDetails && (
                        <Button onClick={onDetails} className={classes.viewAllBlue}>
                          {keyEmptyCheck('view_all_details')}
                          <ADGMImage name="blue_right_angle.svg" alt="angleRight" />
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Hidden>
              </div>
            }
          />
        </ListItem>
      </div>
    </>
  );
}

PropertySummary.propTypes = {
  classes: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  property: PropTypes.object.isRequired,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onDetails: PropTypes.func,
  onDownload: PropTypes.func,
  hasMarginBottom: PropTypes.bool,
};

PropertySummary.defaultProps = {
  expandedId: null,
  selected: null,
  isPMA: false,
  viewPMAUnits: () => {},
  viewLeaseUnitsHandler: () => {},
  isPMABank: false,
  isPmc: false,
  onDelete: null,
  onEdit: null,
  onDetails: null,
  onDownload: null,
  hasMarginBottom: true,
};

export default withStyles(styles)(injectIntl(memo(PropertySummary)));
